/**
 * 优惠券列表相关接口
 */

import { env } from '@hb/mbridge';
import { ResData, httpMgr } from '../../common/network/httpMgr';
import {
  isWeixin, isAli, isBoComTinyApp, getSystemCode,
} from '../../common/env/env';
import { ApiEnums } from '../../constants/apiConstants';
import nativeMgr from '../../common/native/native';
import { appQueryConfig } from '../../common/utils/url';
import type { SkuDetailData } from '../store/modules/ticket';
import logger from '../../common/utils/logger';

export async function requestCategoryList() {
  try {
    let cityInfo = null;
    try {
      cityInfo = await nativeMgr.getCityInfo();
    } catch (error) {
      logger('[bikeMall] ❌ 求类目获取定位信息异常:', error);
      // 定位失败不影响接口请求，继续执行
    }

    const res = await httpMgr.request(ApiEnums.GET_CATEGORY_LIST, {
      cityCode: cityInfo?.cityCode,
      adCode: cityInfo?.adCode,
      channelId: (() => {
        if (isAli) {
          return 2;
        }
        if ((env as any).isTTMP) {
          return 6;
        }

        // 交银小程序
        if (isBoComTinyApp) {
          return 9;
        }

        return env.isEasyBikeApp ? 1 : 3;
      })(),
      adSource: appQueryConfig.adsource,
    });
    // 测试抖音免密签约商品
    // res.data.push({
    //   isDeleted: 0,
    //   categoryId: '3391',
    //   categoryName: '学生中心-单车',
    //   categoryParentId: '0',
    //   displayPlace: 1,
    //   leafNode: 1,
    //   status: 1,
    //   statusStr: '展示',
    //   sortNum: 0,
    //   level: 3,
    //   businessId: 5,
    // });
    return res.data;
  } catch (error) {
    logger('[bikeMall] ❌ 请求类目列表失败:', error);
    // 抛出异常，让调用方决定如何处理（保持原有数据或显示错误提示）
    throw error;
  }
}

export type ReqRequestSkuList = {
  /** 1 APP ,2支付宝,不传默认app */
  channelId?: number;
  /** 类目 */
  categoryId?: string;
  /** 页号 */
  pageNum: number;
  /** 每页数量 */
  pageSize: number;
  /** 城市code */
  cityCode?: string;
  adCode?: string;
  /** 是否骑游 */
  riding?: boolean;
  /** 是否走老限时购 */
  isNew?: boolean;
  /** 是否需要添加两轮卡商品 */
  needTwoWheel?: boolean;
  /** 版本号（两轮卡必传） */
  version?: string;
  /** 纬度（两轮卡必传） */
  lat?: string;
  /** 经度（两轮卡必传） */
  lng?: string;
  /** 用户guid（两轮卡必传） */
  _uuid?: string;
   /** 页面来源 */
  applicationScene?: string;
  /** 走算法标识 */
  algorithmPriceFlag?: string;
  /** 是否需要超值推荐 */
  needGreatValue?: boolean;
  /** 需要从列表中排除的skuId数组（超值推荐已展示的商品） */
  removeFromTopSkuIds?: string[];
}

export const isHarmonyApp = /OpenHarmony|HarmonyOS|hmOS/.test(
  window.navigator.userAgent,
);
// 请求商品列表
export async function requestSkuList(params: ReqRequestSkuList) {
  const cityInfo = await nativeMgr.getCityInfo();

  if (params.categoryId === 'groupon') {
    let channelId = 1; // h5普通浏览器
    if (env.isEasyBikeApp) {
      channelId = 4;
    } else if (isWeixin) {
      channelId = 2;
    } else if (env.isAliMP) {
      channelId = 9;
    } else if (env.isAlipayBrowser) {
      channelId = 3;
    } else if (env.isQQ) {
      channelId = 6;
    } else if (env.isWeibo) {
      channelId = 7;
    } else if (isHarmonyApp) {
      channelId = 8;
    }

    const {
      cityCode = '021',
      adCode = '310112',
    } = cityInfo || {};
    const grouponParams = {
      ...params,
      cityCode,
      adCode,
      channelId,
    };
    const res = await httpMgr.request(ApiEnums.GET_GROUPON_LIST, grouponParams);
    const list = res.data;

    for (let i = 0; i < list.length; ++i) {
      const originSku = list[i].skuDetail[0] || {};

      const finalSku = {
        ...originSku,
        goodsName: list[i].itemAliasName, // 商品展示名
        goodsEquitys: originSku.skuVirtualList,
        categoryId: 'groupon',
        shortRemark: list[i].groupTag,
        originalPrice: originSku.salePrice, // 拼团的原价为券商城的购买价格
      };

      const final = {
        ...finalSku,
        ...list[i],
      };
      if (final.listPicUrl && final.listPicUrl.indexOf('https') < 0) {
        final.listPicUrl = final.listPicUrl.replace('http', 'https');
      }
      list[i] = final;
    }

    return res;
  }

  const {
    cityCode,
    adCode,
    latitude,
    longitude,
  } = cityInfo || {};
  const reqApi = params.riding ? 'GET_LAUNCH_LIST' : 'GET_SKU_COMBINE_LIST';

  // 全部tab,顺风车tab传入算法标识, 页面来源
  if (!params.riding && ['104', '0'].includes(params.categoryId)) {
    params.algorithmPriceFlag = '1';
    params.applicationScene = 'couponMall'; // 卡券商城固定couponMall
  }
  const requestPromise = [httpMgr.request(ApiEnums[reqApi], {
    ...params,
    needGroupGoods: true, // 是否需要拼团商品
    cityCode,
    adCode,
    lat: latitude,
    lng: longitude,
    adSource: appQueryConfig.adsource,
    // 始终走优惠决策
    includeOption: {
      includePromo: true,
    },
  })];
  /**
   * 限制在第一页
   */
  // todo 20220908
  if (params.pageNum === 1 && !params.riding && !params.isNew) {
    requestPromise.push(httpMgr.request(ApiEnums.SKU_TIMELIMIT_LIST, Object.assign(params, {
      cityCode: cityInfo?.cityCode,
      adCode: cityInfo?.adCode,
      pageSize: 3,
    })).catch(noop));
  }

  return Promise.all(requestPromise).then(([listData, skuData]) => {
    if (listData && listData.data) {
      if (skuData && skuData.data && skuData.data.length) {
        listData.data = listData.data.filter((i: any) => {
          const r = skuData.data.find((s: any) => i.skuId === s.skuId);
          return !r;
        });
        listData.data.unshift(...skuData.data);
      }
      listData.data = listData.data.map((item: any) => {
        if (item.listPicUrl && item.listPicUrl.indexOf('https') < 0) {
          item.listPicUrl = item.listPicUrl.replace('http', 'https');
        }
        return item;
      });
    }
    return listData;
  });
}

export type RequestGrouponList = {
  channelId?: number // 1 APP ,2支付宝,不传默认app
  categoryId?: number; // 类目，全部不传
  pageNum: number; // 页号
  pageSize: number; // 每页数量
}
// 请求拼团商品列表
export function requestGrouponList(params: RequestGrouponList) {
  return httpMgr.request(ApiEnums.GET_GROUPON_LIST, params);
}

export type GreatList = {
  channelId?: number // 1 APP ,2支付宝,不传默认app
  pageNum: number; // 页号
  pageSize: number; // 每页数量
  cityCode:string,
  lat:string,
  lng:string,
  systemCode:string
}
// 获取超值模块商品数据
export async function getGreatList() {
  const cityInfo = await nativeMgr.getCityInfo();
  let channelId = 1; // h5普通浏览器
  if (env.isEasyBikeApp) {
    channelId = 1;
  } else if (env.isAliMP) {
    channelId = 2;
  }
  const {
    cityCode,
    latitude,
    longitude,
  } = cityInfo || {};
  const grouponParams = {
    cityCode,
    lat: latitude,
    lng: longitude,
    systemCode: getSystemCode() || 63,
    channelId,
    pageNum: 1,
    pageSize: 10,
    algorithmPriceFlag: 1,
    applicationScene: 'couponMall',
  };

  return httpMgr.request(ApiEnums.GET_GREAT_VALUE_LIST, grouponParams);
}

export type ReqRequestSkuDetail = {
  /** spu编码 */
  spuId: string;
  algorithmPriceFlag?: string; // 是否算法定价
  applicationScene?: string; // 页面来源(算法测有用)
  buyNum?: number;
}

export type QueryCouponGoodsProductListReq = {
  ids?: string[]
}

// 请求商品详情
export async function requestSkuDetail(params: ReqRequestSkuDetail): Promise<SkuDetailData | null> {
  const cityInfo = await nativeMgr.getCityInfo();
  const { data } = await httpMgr.request(ApiEnums.GET_SKU_DETAIL, Object.assign(params, {
    cityCode: cityInfo?.cityCode,
    adCode: cityInfo?.adCode,
    // 始终走优惠决策
    includeOption: {
      includePromo: true,
    },
  }));
  const picUrl = data?.picUrl;

  if (picUrl && !picUrl.includes('https')) {
    return {
      ...data,
      picUrl: picUrl.replace('http', 'https'),
    };
  }

  return data;
}

export async function QueryCouponGoodsProductDetailList(params: QueryCouponGoodsProductListReq) {
  const cityInfo = await nativeMgr.getCityInfo();
  const res = await httpMgr.request(ApiEnums.GET_COUPONGOODS, Object.assign(params, {
    cityCode: cityInfo?.cityCode,
    adCode: cityInfo?.adCode,
    ids: params.ids || [],
  }));
  return res.data;
}

export interface CombiProduct {
  skuId: string;
  spuId: string;
  /** 售价 */
  salePrice: number;
  /** 原价 */
  originalPrice: number;
  /** 商品名称 */
  goodsName: string;
  /** 归属类目 */
  categoryId: string;
  algorithmPriceFlag?: string;
  /** 所属业务线 */
  businessId: string;
}

export type ReqRequestBuyGoods = {
  orderPort?: number // 购买订单渠道
  userNewId?: number | string; // 用户id
  userGuid?: string
  skuId?: string;
  spuId?: string;
  purchaseNum?: number; // 购买数量
  salePrice?: number; // 售价
  payAmount?: number; // 支付金额
  picUrl?: string; // 图片
  cityCode?: string; // 城市code
  adCode?: string; // adCode
  version?: string; // app版本
  systemCode?: string; // 系统编号
  activityCode?: string;
  /** 订单id 特别注意。。。当对已生成的订单进行支付的时候，需要传这个参数进行订单的检查 */
  orderId?: string;
  algorithmPriceFlag?: string; // 是否算法定价 1, 0
  marketActivityCode?: string; // 活动 code
  deviceHash?: string; // 风控下单参数
  appId?: string; // 微信小程序 appId
  pointDiscountAmount?: number; // 超级会员抵扣金额
  isSuperUser?: boolean; // 是否超级会员
  combiProductReqParams?: Exclude<ReqRequestBuyGoods, 'combiProductReqParams'>;
}

// 购买商品
export async function requestBuyGoods(params: ReqRequestBuyGoods) {
  logger('重新支付流程 requestBuyGoods方法 params：', params);
  const cityInfo = await nativeMgr.getCityInfo();
  const { combiProductReqParams, ...restParams } = params;
  logger('requestBuyGoods方法 combiProductReqParams', combiProductReqParams);
  const commonParams = {
    lat: cityInfo?.latitude || '',
    lon: cityInfo?.longitude || '',
    cityCode: cityInfo?.cityCode,
    adCode: cityInfo?.adCode,
    pageSource: appQueryConfig.adsource,
  };
  const resolvedParams = {
    ...restParams,
    ...commonParams,
  };
  let res;

  if (combiProductReqParams) {
    logger('requestBuyGoods方法 combiProductReqParams有值，调用此方法', ApiEnums.BUY_COMBI_GOODS);
    res = await httpMgr.request(ApiEnums.BUY_COMBI_GOODS, {
      mainOrderConfirmQuery: resolvedParams,
      tyingOrderConfirmQuery: {
        ...commonParams,
        ...combiProductReqParams,
      },
    });
  } else {
    res = await httpMgr.request(ApiEnums.BUY_GOODS, resolvedParams);
  }

  return res.data;
}

export interface BuyMultiGoodsItem {
  skuId: string;
  spuId: string;
  num?: number;
  salePrice: number;
  /** 价格类型，1基础价,2拼团价,3秒杀价 */
  priceType?: number;
  /** 奖励金抵扣金额 */
  pointDiscountAmount?: number;
  /** 优惠信息 */
  promotionInfos?: {
    /** 券ID */
    promotionId: string; // 券ID,当couponPromoType=1时存在
    /** 券名称 */
    promotionName: string; // 券名称
    /** 卡券商城优惠类型， 1-营销优惠 2-奖励金 */
    couponPromoType: number; // 卡券商城优惠类型， 1-营销优惠 2-奖励金
    /** 优惠金额 */
    worth: number; // 优惠金额
    /** 可用积分数量，当couponPromoType=2时存在 */
    points: number;
  }[];
  /** 商品额外信息 */
  itemExtra?: {
    /** 是否搭售商品 */
    isTyingFree: boolean;
  };
}

export interface BuyMultiGoodsReqParams {
  /** 重新下单支付时需要 */
  orderId?: string;
  payAmount: number;
  itemList: BuyMultiGoodsItem[];
  /** 是否信用下单 */
  isCreditAuthOrder?: boolean;
  /** 是否已签约 */
  isSigned?: boolean;
  /** 支付类型 */
  chargeType?: number;
  /** 订单类型 */
  orderType?: number;
  /** 连续包月场景 */
  couponMonthlyScene?: string;
  /** 免密签约场景 */
  couponPasswordFreeScene?: string;
  /** 是否隐藏订单 */
  hideOrder?: boolean;
  /** 额外参数 */
  extra?: {
    /** 签约支付信息 */
    payInfo?: string;
  };
}

export interface BuyMultiGoodsRes {
  /** 支付订单号 */
  payOrderId: string;
  /** 卡券商城订单号 */
  orderId: string;
  businessType: string;
  businessScene: string;
  /** 支付金额 */
  payMoney: string;
}

/** 多商品下单 */
export async function reqBuyMultiGoods(
  params: BuyMultiGoodsReqParams,
): Promise<BuyMultiGoodsRes> {
  const cityInfo = await nativeMgr.getCityInfo();
  const finalParams = {
    ...params,
    cityCode: cityInfo?.cityCode,
    adCode: cityInfo?.adCode,
    lat: cityInfo?.latitude || '',
    lon: cityInfo?.longitude || '',
    pageSource: appQueryConfig.adsource,
    orderPort: 1,
  };
  logger('reqBuyMultiGoods-finalParams', JSON.stringify(finalParams));
  const res = await httpMgr.request(ApiEnums.BUY_MULTI_GOODS, finalParams);
  return res.data;
}

/** 限购购买商品——仅返回限购凭证 */
export async function requestBuyGoodslimit(params: ReqRequestBuyGoods): Promise<string> {
  const cityInfo = await nativeMgr.getCityInfo();
  const res = await httpMgr.request(ApiEnums.PURCHASE_GOODS, {
    ...params,
    latitude: cityInfo ? cityInfo.latitude : '',
    longitude: cityInfo ? cityInfo.longitude : '',
    cityCode: cityInfo?.cityCode,
    adCode: cityInfo?.adCode,
    pageSource: appQueryConfig.adsource,
  });
  return res.data;
}

/** 限时购商品下单结果轮询接口 */
export async function reqLimitOrderRes(params: Record<string, unknown>) {
  const res = await httpMgr.request(ApiEnums.TIMELIMIT_ORDER_QUERY, params);
  return res.data;
}

// 支付宝小程序确认支付, 微信确认支付
export async function getPayInfo(params: Record<string, unknown>) {
  const finalParams = {
    needLogin: true,
    noTag: true,
    ...params,
  };
  const res = await httpMgr.request(ApiEnums.GET_PAY_INFO, finalParams);
  return res.data;
}

// 获取推荐商品
export async function getRecomendList(params: Record<string, unknown>) {
  const cityInfo = await nativeMgr.getCityInfo();
  const finalParams = {
    ...params,
    saleCityCode: cityInfo?.cityCode,
  };
  const res = await httpMgr.request(ApiEnums.GET_RECOMMENDLIST, finalParams);
  return res;
}

// 获取新限时购商品
export async function getNewLimitList() {
  const cityInfo = await nativeMgr.getCityInfo();
  const finalParams = {
    channelId: 1,
    cityCode: cityInfo ? cityInfo?.cityCode : '021',
    adCode: cityInfo ? cityInfo.adCode : '310112',
  };
  const res = await httpMgr.request(ApiEnums.NEW_SKU_TIMELIMIT_LIST, finalParams);
  // 场次小于三个直接清空数组，不需要过滤同时在下面展示
  if (res.code === 0) {
    if (Array.isArray(res.data)) {
      if (!res.data.length) return [];
      const { goodsList = [] } = res.data[0];
      if (goodsList.length < 3) return [];
      return res.data.map((i: any, index: number) => ({
        ...i,
        goodsList: index === 0
          ? i.goodsList.map((g: any) => ({ ...g, started: true }))
          : i.goodsList,
      }));
    }
    return [];
  }
  return [];
}

// 获取推荐商品
export async function getLimitSwitch(params: Record<string, unknown>) {
  return httpMgr.request(ApiEnums.LIMIT_SWITCH, params);
}
// 微信AppId前置校验
export async function wxAppIdOrderPreCheck(params: Record<string, unknown>) {
  return httpMgr.request(ApiEnums.APPID_PRE_CHECK, params);
}
// 校验场次是否存在
export function checkRemindStatus(data: Record<string, unknown>) {
  return httpMgr.request(ApiEnums.CHECK_REMINDER, data);
}
// 设置场次提醒
export function addRemindStatus(data: Record<string, unknown>) {
  return httpMgr.request(ApiEnums.ADD_REMINDER, data);
}

export async function fetchRecmSku(params: Record<string, unknown>) {
  const cityInfo = await nativeMgr.getCityInfo();
  const finalParams = {
    ...params,
    saleCityCode: cityInfo?.cityCode,
  };
  const res = await httpMgr.request(ApiEnums.FETCH_RECM_SKU, finalParams);
  return res;
}

export interface SvipInfo {
  isSuperUser: boolean;
  amount?: number;
}

// 获取超级会员信息
export function getSVipStatus(data?: Record<string, unknown>): Promise<ResData<SvipInfo>> {
  return httpMgr.request(ApiEnums.SUPPER_USER_DISCOUNT, {
    ...data,
  });
}

/** 获取券去使用跳转配置 */
export async function fetchCouponGouseConfig(data: Record<string, unknown>) {
  return httpMgr.request(ApiEnums.FETCH_COUPON_GOUSE_CONFIG, {
    ...data,
  });
}

/** 获取搭售商品信息 */
export async function fetchCombiSellProduct(params: Record<string, unknown>)
: Promise<{ data: CombiProduct }> {
  const cityInfo = await nativeMgr.getCityInfo();
  const finalParams = {
    ...params,
    cityCode: cityInfo?.cityCode,
    adCode: cityInfo?.adCode,
  };
  return httpMgr.request(ApiEnums.FETCH_COMBI_PRODUCT, finalParams);
}

/** 随心配商品 */
export interface FreeComposeProduct {
  /** 商品id——spuId */
  itemId: string;
  /** 商品类目ID */
  categoryId: string;
  /** 商品展示名称 */
  showTitle: string;
  /** 商品展示价格 */
  showName: string;
  /** 展示标签 */
  showTag: string;
  /** 排序标识 */
  order: number;
  /** 业务类型 */
  businessType: string;
  /** 业务名称 */
  businessName: string;
  /** 商品skuId */
  skuId: string;
  /** 原价 */
  originalPrice: number;
  /** 售卖价 */
  salePrice: number;
  /** 优惠金额 */
  promoPrice: number;
  /** 库存 */
  stock: number;
}

export interface FreeComposeProductGroup {
  businessName: string;
  businessType: string;
  list: FreeComposeProduct[];
}

/** 获取搭售商品信息 */
export async function fetchFreeComposeProducts(params: Record<string, unknown> = {})
: Promise<{ data: {
  itemAGroupList: FreeComposeProductGroup[];
  itemBList: FreeComposeProduct[];
} }> {
  const cityInfo = await nativeMgr.getCityInfo();
  const finalParams = {
    ...params,
    cityCode: cityInfo?.cityCode,
    adCode: cityInfo?.adCode,
  };

  return httpMgr.request(ApiEnums.FETCH_FREE_COMPOSE_PRODUCTS, finalParams);
}

/**
 * 查询用户是否已签约
 *
 * @see https://hellobike.yuque.com/alphapay/yszrv0/uglnzz
 */
export async function fetchPayAfterUseSignData(params?: {
  /**
   * 场景，默认
  */
  sceneType?: string;
  /**
   * 签约渠道——alipay: 支付宝免密，wechat: 微信免密，wxpayScore: 微信支付分，zhima: 芝麻先用后付
   *
   * 默认zhima
  */
  signChannel?: string;
}): Promise<{
  /** 0表示查询成功，-1失败 */
  code: number;
  data?: {
    /** 签约状态，0表示已签约 */
    status: number;
  },
}> {
  return httpMgr.request(ApiEnums.PAY_AFTER_USE_STATUS, {
    signChannel: 'zhima',
    sceneType: 'COUPON',
    ...params,
  });
}

/**
 * 查询先享后付订单是否信用冻结成功
 *
 * @see https://yapi.hellobike.cn/project/6782/interface/api/330141
 */
export async function checkIfPayAfterUseSuccess(orderId: string): Promise<boolean> {
  let success = false;

  try {
    const { data } = await httpMgr.request(ApiEnums.PAY_AFTER_USE_FROZEN, { orderId });

    if (data?.orderFrozenSuccess) {
      success = true;
    }
  } catch (e) {
    // noop
  }

  return success;
}

/** 获取心智建设疲劳度
* @see https://yapi.hellobike.cn/project/2657/interface/api/393643
*
*/
export async function checkMentalUserVisit(params: Record<string, unknown>) {
  return httpMgr.request(ApiEnums.MENTAL_CHECK_USER_VISIT, params);
}

/** 随心配商品组合列表
* @see https://yapi.hellobike.cn/project/6782/interface/api/340507
*
*/
export async function fetchFreeComposeProductsList(params: Record<string, unknown>) {
  const cityInfo = await nativeMgr.getCityInfo();
  const finalParams = {
    ...params,
    cityCode: cityInfo?.cityCode || '021', // todo: 调试获取城市code 上线要删掉
    adCode: cityInfo?.adCode || '310112', // todo: 调试获取adCode 上线要删掉
    showServerErr: false, // 不展示服务端业务错误 toast
  };
  return httpMgr.request(ApiEnums.FETCH_FREE_COMPOSE_PRODUCTS_LIST, finalParams);
}
/** 司南的接口来判断是否展示
* @see https://yapi.hellobike.cn/project/3335/interface/api/297494
*
*/
export async function npsIsShow(params: Record<string, unknown>) {
  return httpMgr.request(ApiEnums.NPS_IS_SHOW, params);
}

export default {};
